import type {
    DesignSystemBorderRadius,
    DesignSystemCardDirection,
    DesignSystemCardSize,
} from '../../theme/design-system/types';
import { spacingValues } from '../../theme/design-system/tokens';
import type { DesignSystemLeadItemSize } from '../LeadItem/LeadItem.styleUtils';

export type SizeTokens = {
    padding: string;
    borderRadiusToken: DesignSystemBorderRadius;
    gap: { default: string; vertical: string };
    actionsExtraGap: { default: string; vertical: string };
};

export const sizeTokens: Record<DesignSystemCardSize, SizeTokens> = {
    xs: {
        padding: spacingValues.xl,
        borderRadiusToken: 'xl',
        gap: { default: spacingValues.lg, vertical: spacingValues.md },
        actionsExtraGap: {
            default: spacingValues.xxs,
            vertical: spacingValues.xs,
        },
    },
    sm: {
        padding: spacingValues['2xl'],
        borderRadiusToken: '2xl',
        gap: { default: spacingValues.xl, vertical: spacingValues.lg },
        actionsExtraGap: {
            default: spacingValues.xs,
            vertical: spacingValues.sm,
        },
    },
    md: {
        padding: spacingValues['3xl'],
        borderRadiusToken: '3xl',
        gap: { default: spacingValues['2xl'], vertical: spacingValues.xl },
        actionsExtraGap: {
            default: spacingValues.xs,
            vertical: spacingValues.md,
        },
    },
};

export const leadItemSizeMap: Record<
    DesignSystemCardSize,
    DesignSystemLeadItemSize
> = {
    xs: 'md',
    sm: 'lg',
    md: 'xl',
};

export const GRID_AREA_LEAD = 'card-lead';
export const GRID_AREA_CONTENT = 'card-content';
export const GRID_AREA_ACTIONS = 'card-actions';

function getGap(direction: DesignSystemCardDirection, tokens: SizeTokens) {
    return direction === 'vertical' ? tokens.gap.vertical : tokens.gap.default;
}

export function getActionsStyles(
    direction: DesignSystemCardDirection,
    tokens: SizeTokens,
) {
    switch (direction) {
        case 'vertical-actions':
            return { gridArea: GRID_AREA_ACTIONS };

        case 'vertical':
            return { marginTop: tokens.actionsExtraGap.vertical };

        case 'horizontal':
            return {
                marginLeft: tokens.actionsExtraGap.default,
                alignSelf: 'center',
            };
    }
}

export function getLayoutStyles(
    direction: DesignSystemCardDirection,
    tokens: SizeTokens,
) {
    const gap = getGap(direction, tokens);

    switch (direction) {
        case 'horizontal':
            return {
                display: 'flex' as const,
                flexDirection: 'row' as const,
                alignItems: 'center',
                gap,
            };

        case 'vertical-actions':
            return {
                display: 'grid',
                gridTemplateAreas:
                    `"${GRID_AREA_LEAD} ${GRID_AREA_CONTENT}" ` +
                    `"${GRID_AREA_LEAD} ${GRID_AREA_ACTIONS}"`,
                gridTemplateColumns: 'auto 1fr',
                rowGap: gap,
            };

        case 'vertical':
        default:
            return {
                display: 'flex' as const,
                flexDirection: 'column' as const,
                alignItems: 'flex-start',
                gap,
            };
    }
}
